import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Checkbox,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Payment as PaymentIcon,
  PlayArrow as TriggerIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { commissionService } from '../services/commissionService';
import { vendorService } from '../services/vendorService';
import {
  VendorCommission,
  CommissionStatus,
  CommissionMilestone,
  PaymentDetails,
} from '../types/commission';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const CommissionApproval: React.FC = () => {
  const [commissions, setCommissions] = useState<VendorCommission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCommissions, setSelectedCommissions] = useState<string[]>([]);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openAutoApproveDialog, setOpenAutoApproveDialog] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState<VendorCommission | null>(null);
  const [vendors, setVendors] = useState<any[]>([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  // Filters
  const [milestoneFilter, setMilestoneFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Form data
  const [rejectionReason, setRejectionReason] = useState('');
  const [autoApproveDays, setAutoApproveDays] = useState(7);
  const [paymentData, setPaymentData] = useState<PaymentDetails>({
    paidBy: '',
    paymentMethod: 'Bank Transfer',
    paymentReference: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentNotes: '',
  });

  // Summary stats
  const [stats, setStats] = useState({
    totalPending: 0,
    totalPendingAmount: 0,
    totalApproved: 0,
    totalApprovedAmount: 0,
    totalPaid: 0,
    totalPaidAmount: 0,
    pendingCount: 0,
  });

  const loadCommissions = useCallback(async () => {
    try {
      setLoading(true);

      let statusFilter = '';
      if (selectedTab === 0) statusFilter = CommissionStatus.PENDING;
      else if (selectedTab === 1) statusFilter = CommissionStatus.APPROVED;
      else if (selectedTab === 2) statusFilter = CommissionStatus.PAID;

      const response = await commissionService.getCommissions({
        page: page + 1,
        limit: rowsPerPage,
        status: statusFilter,
        milestone: milestoneFilter || undefined,
        vendorId: vendorFilter || undefined,
      });

      setCommissions(response.data || []);
      setTotalCount(response.pagination?.total || 0);

      // Calculate stats
      if (response.data) {
        const pending = response.data.filter((c: any) => c.status === CommissionStatus.PENDING);
        const approved = response.data.filter((c: any) => c.status === CommissionStatus.APPROVED);
        const paid = response.data.filter((c: any) => c.status === CommissionStatus.PAID);

        setStats({
          totalPending: pending.length,
          totalPendingAmount: pending.reduce((sum: number, c: any) => sum + c.amount, 0),
          totalApproved: approved.length,
          totalApprovedAmount: approved.reduce((sum: number, c: any) => sum + c.amount, 0),
          totalPaid: paid.length,
          totalPaidAmount: paid.reduce((sum: number, c: any) => sum + c.amount, 0),
          pendingCount: pending.length,
        });
      }
    } catch (error) {
      console.error('❌ Error loading commissions:', error);
      setSnackbar({
        open: true,
        message: `Failed to load commissions: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, selectedTab, milestoneFilter, vendorFilter]);

  const loadVendors = useCallback(async () => {
    try {
      const response = await vendorService.getVendors({ limit: 100 });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, []);

  useEffect(() => {
    loadCommissions();
    loadVendors();
  }, [loadCommissions, loadVendors]);

  const handleSelectCommission = (id: string) => {
    setSelectedCommissions((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedCommissions(commissions.map((c) => c.id));
    } else {
      setSelectedCommissions([]);
    }
  };

  const handleApproveCommission = async (commission: VendorCommission) => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId') || 'admin';
      await commissionService.approveCommission(commission.id, userId);
      setSnackbar({
        open: true,
        message: 'Commission approved successfully',
        severity: 'success',
      });
      loadCommissions();
      setSelectedCommissions([]);
    } catch (error) {
      console.error('❌ Error approving commission:', error);
      setSnackbar({
        open: true,
        message: `Failed to approve commission: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedCommissions.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select commissions to approve',
        severity: 'warning',
      });
      return;
    }

    try {
      setLoading(true);
      const userId = localStorage.getItem('userId') || 'admin';
      const result = await commissionService.bulkApproveCommissions(selectedCommissions, userId);
      setSnackbar({
        open: true,
        message: `Approved ${result.data?.successCount || selectedCommissions.length} commissions`,
        severity: 'success',
      });
      loadCommissions();
      setSelectedCommissions([]);
    } catch (error) {
      console.error('❌ Error bulk approving:', error);
      setSnackbar({
        open: true,
        message: `Failed to bulk approve: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRejectDialog = (commission: VendorCommission) => {
    setSelectedCommission(commission);
    setRejectionReason('');
    setOpenRejectDialog(true);
  };

  const handleRejectCommission = async () => {
    if (!selectedCommission || !rejectionReason.trim()) {
      setSnackbar({
        open: true,
        message: 'Please provide a rejection reason',
        severity: 'warning',
      });
      return;
    }

    try {
      setLoading(true);
      const userId = localStorage.getItem('userId') || 'admin';
      await commissionService.rejectCommission(
        selectedCommission.id,
        userId,
        rejectionReason
      );
      setSnackbar({
        open: true,
        message: 'Commission rejected successfully',
        severity: 'success',
      });
      setOpenRejectDialog(false);
      loadCommissions();
    } catch (error) {
      console.error('❌ Error rejecting commission:', error);
      setSnackbar({
        open: true,
        message: `Failed to reject commission: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPayDialog = (commission?: VendorCommission) => {
    if (commission) {
      setSelectedCommissions([commission.id]);
    }
    const userId = localStorage.getItem('userId') || 'admin';
    setPaymentData({
      paidBy: userId,
      paymentMethod: 'Bank Transfer',
      paymentReference: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentNotes: '',
    });
    setOpenPayDialog(true);
  };

  const handleMarkAsPaid = async () => {
    if (selectedCommissions.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select commissions to pay',
        severity: 'warning',
      });
      return;
    }

    if (!paymentData.paymentMethod || !paymentData.paidBy) {
      setSnackbar({
        open: true,
        message: 'Please fill in payment details',
        severity: 'warning',
      });
      return;
    }

    try {
      setLoading(true);
      if (selectedCommissions.length === 1) {
        await commissionService.markCommissionPaid(selectedCommissions[0], paymentData);
      } else {
        await commissionService.bulkPayCommissions(selectedCommissions, paymentData);
      }
      setSnackbar({
        open: true,
        message: `Marked ${selectedCommissions.length} commission(s) as paid`,
        severity: 'success',
      });
      setOpenPayDialog(false);
      loadCommissions();
      setSelectedCommissions([]);
    } catch (error) {
      console.error('❌ Error marking as paid:', error);
      setSnackbar({
        open: true,
        message: `Failed to mark as paid: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAutoApprove = async () => {
    try {
      setLoading(true);
      const result = await commissionService.autoApproveCommissions(autoApproveDays);
      setSnackbar({
        open: true,
        message: `Auto-approved ${result.data?.approvedCount || 0} commissions`,
        severity: 'success',
      });
      setOpenAutoApproveDialog(false);
      loadCommissions();
    } catch (error) {
      console.error('❌ Error auto-approving:', error);
      setSnackbar({
        open: true,
        message: `Failed to auto-approve: ${(error as Error).message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (commission: VendorCommission) => {
    setSelectedCommission(commission);
    setOpenDetailsDialog(true);
  };

  const getMilestoneColor = (milestone: CommissionMilestone): any => {
    switch (milestone) {
      case CommissionMilestone.REGISTRATION:
        return 'default';
      case CommissionMilestone.KYC_VERIFIED:
        return 'info';
      case CommissionMilestone.FIRST_DELIVERY:
        return 'primary';
      case CommissionMilestone.RETAINED_30_DAYS:
        return 'success';
      case CommissionMilestone.RETAINED_90_DAYS:
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: CommissionStatus): any => {
    switch (status) {
      case CommissionStatus.PENDING:
        return 'warning';
      case CommissionStatus.APPROVED:
        return 'info';
      case CommissionStatus.PAID:
        return 'success';
      case CommissionStatus.REJECTED:
        return 'error';
      case CommissionStatus.CANCELLED:
        return 'default';
      default:
        return 'default';
    }
  };

  const getDaysPending = (eligibleAt: Date) => {
    const now = new Date();
    const eligible = new Date(eligibleAt);
    const diffTime = Math.abs(now.getTime() - eligible.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Commission Approval
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<TriggerIcon />}
            onClick={() => setOpenAutoApproveDialog(true)}
          >
            Auto-Approve
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadCommissions}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Total Pending
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                ₹{stats.totalPendingAmount.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {stats.totalPending} commission(s)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Total Approved
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="info.main">
                ₹{stats.totalApprovedAmount.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {stats.totalApproved} commission(s)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Total Paid
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                ₹{stats.totalPaidAmount.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {stats.totalPaid} commission(s)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Pending Count
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {stats.pendingCount}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Awaiting approval
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="Search by vendor or referral"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Milestone</InputLabel>
                <Select
                  value={milestoneFilter}
                  label="Milestone"
                  onChange={(e) => setMilestoneFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value={CommissionMilestone.REGISTRATION}>Registration</MenuItem>
                  <MenuItem value={CommissionMilestone.KYC_VERIFIED}>KYC Verified</MenuItem>
                  <MenuItem value={CommissionMilestone.FIRST_DELIVERY}>First Delivery</MenuItem>
                  <MenuItem value={CommissionMilestone.RETAINED_30_DAYS}>Retained 30 Days</MenuItem>
                  <MenuItem value={CommissionMilestone.RETAINED_90_DAYS}>Retained 90 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Vendor</InputLabel>
                <Select
                  value={vendorFilter}
                  label="Vendor"
                  onChange={(e) => setVendorFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.id}>
                      {vendor.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchTerm('');
                  setMilestoneFilter('');
                  setVendorFilter('');
                }}
                sx={{ height: '56px' }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={selectedTab} onChange={(_, newValue) => setSelectedTab(newValue)}>
          <Tab label="Pending" />
          <Tab label="Approved" />
          <Tab label="Paid" />
        </Tabs>
      </Paper>

      {/* Bulk Actions */}
      {selectedCommissions.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body1">
              {selectedCommissions.length} commission(s) selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {selectedTab === 0 && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={handleBulkApprove}
                >
                  Approve Selected
                </Button>
              )}
              {selectedTab === 1 && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PaymentIcon />}
                  onClick={() => handleOpenPayDialog()}
                >
                  Pay Selected
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Table */}
      <TabPanel value={selectedTab} index={0}>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        commissions.length > 0 &&
                        selectedCommissions.length === commissions.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Referral</TableCell>
                  <TableCell>Milestone</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Days Pending</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : commissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No pending commissions
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  commissions.map((commission) => (
                    <TableRow key={commission.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedCommissions.includes(commission.id)}
                          onChange={() => handleSelectCommission(commission.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {commission.vendor?.name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {commission.vendor?.vendorCode || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {commission.referral?.candidateName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {commission.referral?.referralCode || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={commission.milestone.replace(/_/g, ' ')}
                          color={getMilestoneColor(commission.milestone)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          ₹{commission.amount.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${getDaysPending(commission.eligibleAt)} days`}
                          size="small"
                          color={getDaysPending(commission.eligibleAt) > 14 ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleViewDetails(commission)}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApproveCommission(commission)}
                            >
                              <ApproveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleOpenRejectDialog(commission)}
                            >
                              <RejectIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      </TabPanel>

      <TabPanel value={selectedTab} index={1}>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        commissions.length > 0 &&
                        selectedCommissions.length === commissions.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Referral</TableCell>
                  <TableCell>Milestone</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Approved By</TableCell>
                  <TableCell>Approved Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : commissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No approved commissions
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  commissions.map((commission) => (
                    <TableRow key={commission.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedCommissions.includes(commission.id)}
                          onChange={() => handleSelectCommission(commission.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {commission.vendor?.name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {commission.vendor?.vendorCode || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {commission.referral?.candidateName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {commission.referral?.referralCode || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={commission.milestone.replace(/_/g, ' ')}
                          color={getMilestoneColor(commission.milestone)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          ₹{commission.amount.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>{commission.approvedBy || 'System'}</TableCell>
                      <TableCell>
                        {commission.approvedAt
                          ? new Date(commission.approvedAt).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleViewDetails(commission)}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Mark as Paid">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleOpenPayDialog(commission)}
                            >
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      </TabPanel>

      <TabPanel value={selectedTab} index={2}>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Referral</TableCell>
                  <TableCell>Milestone</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Payment Reference</TableCell>
                  <TableCell>Paid Date</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : commissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No paid commissions
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  commissions.map((commission) => (
                    <TableRow key={commission.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {commission.vendor?.name || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {commission.vendor?.vendorCode || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {commission.referral?.candidateName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {commission.referral?.referralCode || ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={commission.milestone.replace(/_/g, ' ')}
                          color={getMilestoneColor(commission.milestone)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          ₹{commission.amount.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>{commission.paymentMethod || '-'}</TableCell>
                      <TableCell>{commission.paymentReference || '-'}</TableCell>
                      <TableCell>
                        {commission.paidAt
                          ? new Date(commission.paidAt).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleViewDetails(commission)}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Paper>
      </TabPanel>

      {/* Reject Dialog */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Commission</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Commission: {selectedCommission?.referral?.candidateName} - ₹
            {selectedCommission?.amount.toFixed(2)}
          </Typography>
          <TextField
            fullWidth
            required
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            rows={4}
            placeholder="Provide a detailed reason for rejection"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)}>Cancel</Button>
          <Button onClick={handleRejectCommission} variant="contained" color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={openPayDialog} onClose={() => setOpenPayDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Commission as Paid</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {selectedCommissions.length} commission(s) selected
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentData.paymentMethod}
                  label="Payment Method"
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, paymentMethod: e.target.value })
                  }
                >
                  <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                  <MenuItem value="UPI">UPI</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Reference"
                value={paymentData.paymentReference}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, paymentReference: e.target.value })
                }
                placeholder="Transaction ID, UTR, etc."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="date"
                label="Payment Date"
                value={paymentData.paymentDate}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, paymentDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Notes"
                value={paymentData.paymentNotes}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, paymentNotes: e.target.value })
                }
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPayDialog(false)}>Cancel</Button>
          <Button onClick={handleMarkAsPaid} variant="contained" color="success">
            Mark as Paid
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auto-Approve Dialog */}
      <Dialog
        open={openAutoApproveDialog}
        onClose={() => setOpenAutoApproveDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Auto-Approve Commissions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Automatically approve all commissions older than specified days.
          </Typography>
          <TextField
            fullWidth
            required
            type="number"
            label="Days Old"
            value={autoApproveDays}
            onChange={(e) => setAutoApproveDays(parseInt(e.target.value))}
            inputProps={{ min: 1, max: 90 }}
            helperText="Approve commissions pending for more than this many days"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAutoApproveDialog(false)}>Cancel</Button>
          <Button onClick={handleAutoApprove} variant="contained" color="primary">
            Auto-Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Commission Details</DialogTitle>
        <DialogContent>
          {selectedCommission && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Chip
                  label={selectedCommission.status}
                  color={getStatusColor(selectedCommission.status)}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Vendor
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedCommission.vendor?.name || 'N/A'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedCommission.vendor?.vendorCode || ''}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Referral
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {selectedCommission.referral?.candidateName || 'N/A'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedCommission.referral?.referralCode || ''}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Milestone
                </Typography>
                <Typography variant="body1">
                  {selectedCommission.milestone.replace(/_/g, ' ')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Amount
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  ₹{selectedCommission.amount.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  Eligible Date
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedCommission.eligibleAt).toLocaleString()}
                </Typography>
              </Grid>
              {selectedCommission.approvedAt && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Approved By
                    </Typography>
                    <Typography variant="body1">{selectedCommission.approvedBy}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Approved Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedCommission.approvedAt).toLocaleString()}
                    </Typography>
                  </Grid>
                </>
              )}
              {selectedCommission.paidAt && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Paid By
                    </Typography>
                    <Typography variant="body1">{selectedCommission.paidBy}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Paid Date
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedCommission.paidAt).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Payment Method
                    </Typography>
                    <Typography variant="body1">{selectedCommission.paymentMethod}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Payment Reference
                    </Typography>
                    <Typography variant="body1">
                      {selectedCommission.paymentReference || '-'}
                    </Typography>
                  </Grid>
                </>
              )}
              {selectedCommission.rejectedAt && (
                <>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Rejected By
                    </Typography>
                    <Typography variant="body1">{selectedCommission.rejectedBy}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary">
                      Rejection Reason
                    </Typography>
                    <Typography variant="body1">{selectedCommission.rejectionReason}</Typography>
                  </Grid>
                </>
              )}
              {selectedCommission.notes && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Notes
                  </Typography>
                  <Typography variant="body1">{selectedCommission.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionApproval;
